import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Drawer,
  Fab,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SegmentedControls from "./Reusables/SegmentedControls";
import PaymentProcessing from "./Orders/PaymentProcessing";
import CartItemCard from "./Reusables/CartItemCard";
import {
  aggregateCartTaxSummary,
  calculateTaxAmount,
  toMoneyNumber,
} from "../utils/tax";
import { API } from "../utils/Api";
import {
  addTrackedOrder,
  getOrderNumber,
  getOrderStatusKey,
} from "../utils/lastOrder";
import { clearPendingPayment } from "../utils/pendingPayment";
import axios from "axios";

const LOCAL_CART_KEY = "quickOrderCart";
const LOCAL_CART_OUTLET_KEY = "quickOrderCartOutletId";
const LOCAL_CART_DATE_KEY = "quickOrderCartDate";

const getLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMsUntilNextLocalMidnight = () => {
  const now = new Date();
  const nextMidnight = new Date(now);

  nextMidnight.setHours(24, 0, 0, 0);

  return Math.max(nextMidnight.getTime() - now.getTime(), 0);
};

export const clearLocalCart = () => {
  localStorage.setItem(LOCAL_CART_KEY, "[]");
  localStorage.removeItem(LOCAL_CART_OUTLET_KEY);
  localStorage.removeItem(LOCAL_CART_DATE_KEY);
};

const expireStoredCartIfStale = (currentOutletId) => {
  const storedCartDate = localStorage.getItem(LOCAL_CART_DATE_KEY);
  const today = getLocalDateKey();

  if (storedCartDate === today) {
    return false;
  }

  if (!storedCartDate) {
    try {
      const storedCart = JSON.parse(
        localStorage.getItem(LOCAL_CART_KEY) || "[]",
      );
      const hasItems = Array.isArray(storedCart) && storedCart.length > 0;

      if (hasItems) {
        localStorage.setItem(LOCAL_CART_DATE_KEY, today);
      }
    } catch (error) {
      console.error("Invalid local cart:", error);
    }

    return false;
  }

  clearLocalCart();

  if (currentOutletId) {
    localStorage.setItem(LOCAL_CART_OUTLET_KEY, currentOutletId);
  }

  return true;
};

const readStoredCart = (currentOutletId) => {
  try {
    expireStoredCartIfStale(currentOutletId);

    const storedCartOutletId = localStorage.getItem(LOCAL_CART_OUTLET_KEY);

    if (
      currentOutletId &&
      storedCartOutletId &&
      storedCartOutletId !== currentOutletId
    ) {
      clearLocalCart();
      localStorage.setItem(LOCAL_CART_OUTLET_KEY, currentOutletId);
      return [];
    }

    const storedCart = JSON.parse(localStorage.getItem(LOCAL_CART_KEY));

    return Array.isArray(storedCart) ? storedCart : [];
  } catch (error) {
    console.error("Invalid local cart:", error);

    return [];
  }
};

const formatCurrency = (value) => `₹${Number(value || 0)}`;

const getAddonId = (addon) =>
  addon?._id ||
  addon?.id ||
  addon?.addonId ||
  addon?.productId ||
  addon?.name ||
  addon?.addonName;

const getAddonName = (addon) => {
  const valueName =
    addon?.label ||
    addon?.name ||
    addon?.addonName ||
    addon?.productName ||
    addon?.title;

  const addonTitle = addon?.addonTitle;

  if (valueName && addonTitle && addonTitle !== valueName) {
    return `${valueName}`;
  }

  if (valueName) {
    return valueName;
  }

  return addonTitle || "Addon";
};

const getAddonPrice = (addon) =>
  Number(
    addon?.price ?? addon?.addonPrice ?? addon?.unitPrice ?? addon?.amount ?? 0,
  );

const createCartSignature = (productId, addons = []) => {
  const addonIds = addons.map(getAddonId).filter(Boolean).map(String).sort();

  return `${productId}__${addonIds.join("_")}`;
};

const normalizeCartItemTax = (cartItem) => {
  if (
    cartItem?.taxable != null &&
    cartItem?.taxAmount != null &&
    cartItem?.grandTotal != null
  ) {
    return cartItem;
  }

  const taxCalculation = calculateTaxAmount(
    cartItem?.unitPrice || 0,
    cartItem?.quantity || 1,
    cartItem?.productTaxPercent || 0,
  );

  return {
    ...cartItem,
    productTaxPercent: cartItem?.productTaxPercent || 0,
    taxable: taxCalculation.taxable,
    taxAmount: taxCalculation.taxAmount,
    grandTotal: taxCalculation.grandTotal,
    totalPrice: taxCalculation.grandTotal,
  };
};

const applyTaxFieldsToCartItem = (
  cartItem,
  quantity,
  unitPrice,
  productTaxPercent,
) => {
  const taxCalculation = calculateTaxAmount(
    unitPrice,
    quantity,
    productTaxPercent,
  );

  return {
    ...cartItem,
    quantity,
    unitPrice,
    productTaxPercent,
    taxable: taxCalculation.taxable,
    taxAmount: taxCalculation.taxAmount,
    grandTotal: taxCalculation.grandTotal,
    totalPrice: taxCalculation.grandTotal,
  };
};

const CartDrawer = forwardRef(
  ({ outletData, onMessage, onEditCartItem, onCartChange }, ref) => {
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);

    const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
    const [checkingStock, setCheckingStock] = useState(false);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [cartProductRawMaterials, setCartProductRawMaterials] = useState(
      [],
    );

    const [animateCartButton, setAnimateCartButton] = useState(false);

    const [selectedOrderType, setSelectedOrderType] = useState(() => {
      const savedOrderType =
        localStorage.getItem("selectedOrderType") ||
        localStorage.getItem("orderType") ||
        "DINE_IN";

      return savedOrderType === "TAKE-AWAY" ? "TAKE-AWAY" : "DINE_IN";
    });

    const cartOutletIdRef = useRef(outletData?._id);

    const [cart, setCart] = useState(() => readStoredCart(outletData?._id));

    const notify = (text, severity = "success") => {
      onMessage?.(text, severity);
    };

    const saveCart = (updatedCart) => {
      setCart(updatedCart);

      localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(updatedCart));

      if (outletData?._id) {
        localStorage.setItem(LOCAL_CART_OUTLET_KEY, outletData._id);
      }

      if (Array.isArray(updatedCart) && updatedCart.length > 0) {
        localStorage.setItem(LOCAL_CART_DATE_KEY, getLocalDateKey());
      } else {
        localStorage.removeItem(LOCAL_CART_DATE_KEY);
      }
    };

    const addItem = (item) => {
      if (!item?.productId) {
        notify("Unable to add this product.", "error");

        return;
      }

      const itemQuantity = Math.max(Number(item?.quantity || 1), 1);

      const signature = createCartSignature(item.productId, item.addons);

      const existingIndex = cart.findIndex(
        (cartItem) => cartItem?.signature === signature,
      );

      const unitPrice = toMoneyNumber(item?.unitPrice || 0);

      const productTaxPercent = Number(item?.productTaxPercent || 0);

      const updatedCart = [...cart];

      if (existingIndex >= 0) {
        const existingItem = updatedCart[existingIndex];

        const updatedQuantity =
          Number(existingItem?.quantity || 0) + itemQuantity;

        updatedCart[existingIndex] = applyTaxFieldsToCartItem(
          existingItem,
          updatedQuantity,
          existingItem?.unitPrice || unitPrice,
          existingItem?.productTaxPercent ?? productTaxPercent,
        );
      } else {
        updatedCart.push(
          applyTaxFieldsToCartItem(
            {
              ...item,
              cartItemId: `${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 9)}`,
              signature,
              hasAddon:
                Boolean(item?.hasAddon) ||
                (Array.isArray(item?.addons) && item.addons.length > 0),
              hasAddons: Array.isArray(item?.addons) && item.addons.length > 0,
              orderType: selectedOrderType,
            },
            itemQuantity,
            unitPrice,
            productTaxPercent,
          ),
        );
      }

      saveCart(updatedCart);
    };

    const cartSummary = useMemo(
      () => aggregateCartTaxSummary(cart.map(normalizeCartItemTax)),
      [cart],
    );

    const discountAmount = 0;

    const cartTaxable = cartSummary.taxable;

    const taxAmount = cartSummary.taxAmount;

    const grandTotal = cartSummary.grandTotal;

    const cartTotal = grandTotal;

    const cartQuantity = useMemo(
      () =>
        cart.reduce(
          (total, cartItem) => total + Number(cartItem?.quantity || 0),
          0,
        ),
      [cart],
    );

    const updateCartQuantity = (cartItemId, quantityChange) => {
      const updatedCart = cart
        .map((cartItem) => {
          if (cartItem?.cartItemId !== cartItemId) {
            return cartItem;
          }

          const updatedQuantity =
            Number(cartItem?.quantity || 1) + quantityChange;

          if (updatedQuantity <= 0) {
            return null;
          }

          return applyTaxFieldsToCartItem(
            cartItem,
            updatedQuantity,
            cartItem?.unitPrice || 0,
            cartItem?.productTaxPercent || 0,
          );
        })
        .filter(Boolean);

      saveCart(updatedCart);
    };

    const findProductCartTarget = (productId) => {
      if (!productId) {
        return null;
      }

      const matchingItems = cart
        .map((cartItem, index) => ({
          cartItem,
          index,
        }))
        .filter(({ cartItem }) => cartItem?.productId === productId);

      if (matchingItems.length === 0) {
        return null;
      }

      return (
        matchingItems.find(
          ({ cartItem }) =>
            !cartItem?.hasAddons &&
            (!Array.isArray(cartItem?.addons) || cartItem.addons.length === 0),
        ) || matchingItems[matchingItems.length - 1]
      );
    };

    const decreaseProduct = (productId) => {
      const target = findProductCartTarget(productId);

      if (!target) {
        return;
      }

      updateCartQuantity(target.cartItem.cartItemId, -1);
    };

    const increaseProduct = (productId) => {
      const target = findProductCartTarget(productId);

      if (!target) {
        return;
      }

      updateCartQuantity(target.cartItem.cartItemId, 1);
    };

    useImperativeHandle(ref, () => ({
      addItem,
      openCart: () => setOpen(true),
      closeCart: () => setOpen(false),
      clearCart: () => saveCart([]),
      decreaseProduct,
      increaseProduct,
    }));

    useEffect(() => {
      onCartChange?.(cart);
    }, [cart, onCartChange]);

    useEffect(() => {
      const nextOutletId = outletData?._id;

      if (!nextOutletId) {
        return;
      }

      if (cartOutletIdRef.current && cartOutletIdRef.current !== nextOutletId) {
        setCart([]);
        setCartProductRawMaterials([]);
        setOpen(false);
        setPaymentDrawerOpen(false);
        clearLocalCart();
        localStorage.setItem(LOCAL_CART_OUTLET_KEY, nextOutletId);
      } else if (!localStorage.getItem(LOCAL_CART_OUTLET_KEY)) {
        localStorage.setItem(LOCAL_CART_OUTLET_KEY, nextOutletId);
      }

      cartOutletIdRef.current = nextOutletId;
    }, [outletData?._id]);

    useEffect(() => {
      let timeoutId;

      const expireCartForNewDay = () => {
        if (!expireStoredCartIfStale(outletData?._id)) {
          return;
        }

        setCart([]);
        setCartProductRawMaterials([]);
        setOpen(false);
        setPaymentDrawerOpen(false);
      };

      const scheduleMidnightExpiry = () => {
        timeoutId = window.setTimeout(() => {
          expireCartForNewDay();
          scheduleMidnightExpiry();
        }, getMsUntilNextLocalMidnight() + 250);
      };

      expireCartForNewDay();
      scheduleMidnightExpiry();

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          expireCartForNewDay();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("focus", expireCartForNewDay);

      return () => {
        window.clearTimeout(timeoutId);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        window.removeEventListener("focus", expireCartForNewDay);
      };
    }, [outletData?._id]);

    const removeCartItem = (cartItemId) => {
      saveCart(cart.filter((cartItem) => cartItem?.cartItemId !== cartItemId));
    };

    const applyCartItemCustomization = (cartItemId, updates) => {
      const currentItem = cart.find(
        (cartItem) => cartItem?.cartItemId === cartItemId,
      );

      if (!currentItem) {
        return;
      }

      const nextAddons = Array.isArray(updates?.addons)
        ? updates.addons
        : [];
      const nextQuantity = Math.max(Number(updates?.quantity || 1), 1);
      const nextUnitPrice = toMoneyNumber(
        updates?.unitPrice ?? currentItem?.unitPrice ?? 0,
      );
      const nextTaxPercent = Number(
        updates?.productTaxPercent ?? currentItem?.productTaxPercent ?? 0,
      );
      const nextSignature = createCartSignature(
        updates?.productId || currentItem.productId,
        nextAddons,
      );

      const nextItem = applyTaxFieldsToCartItem(
        {
          ...currentItem,
          ...updates,
          addons: nextAddons,
          signature: nextSignature,
          hasAddon: Boolean(updates?.hasAddon) || nextAddons.length > 0,
          hasAddons: nextAddons.length > 0,
        },
        nextQuantity,
        nextUnitPrice,
        nextTaxPercent,
      );

      const duplicateItem = cart.find(
        (cartItem) =>
          cartItem?.cartItemId !== cartItemId &&
          cartItem?.signature === nextSignature,
      );

      if (duplicateItem) {
        const mergedCart = cart
          .map((cartItem) => {
            if (cartItem?.cartItemId === duplicateItem.cartItemId) {
              return applyTaxFieldsToCartItem(
                cartItem,
                Number(cartItem?.quantity || 0) + nextQuantity,
                cartItem?.unitPrice || nextUnitPrice,
                cartItem?.productTaxPercent ?? nextTaxPercent,
              );
            }

            return cartItem;
          })
          .filter((cartItem) => cartItem?.cartItemId !== cartItemId);

        saveCart(mergedCart);
        return;
      }

      saveCart(
        cart.map((cartItem) =>
          cartItem?.cartItemId === cartItemId ? nextItem : cartItem,
        ),
      );
    };

    const clearCart = () => {
      saveCart([]);
      setCartProductRawMaterials([]);
      notify("Cart cleared.");
    };

    useEffect(() => {
      if (cartQuantity <= 0) {
        setOpen(false);
        return undefined;
      }

      setAnimateCartButton(false);

      const frameId = window.requestAnimationFrame(() => {
        setAnimateCartButton(true);
      });

      const timeoutId = window.setTimeout(() => {
        setAnimateCartButton(false);
      }, 650);

      return () => {
        window.cancelAnimationFrame(frameId);

        window.clearTimeout(timeoutId);
      };
    }, [cartQuantity]);

    const handleOrderTypeChange = (nextOrderType) => {
      setSelectedOrderType(nextOrderType);

      localStorage.setItem("selectedOrderType", nextOrderType);
    };

    const handleClosePaymentDrawer = () => {
      if (isCreatingOrder) return;

      setPaymentDrawerOpen(false);
      setOpen(true);
    };

    const handleOrderCreated = (createdOrder, razorpayOrderId) => {
      const resolvedRazorpayOrderId =
        razorpayOrderId ||
        createdOrder?.razorpayOrderId ||
        createdOrder?.razorpay_order_id ||
        null;

      addTrackedOrder(outletData?._id, resolvedRazorpayOrderId, {
        orderNumber: getOrderNumber(createdOrder),
        status: getOrderStatusKey(createdOrder),
        placedAt: createdOrder?.createdAt || new Date().toISOString(),
      });
      clearPendingPayment(resolvedRazorpayOrderId);
      saveCart([]);
      setCartProductRawMaterials([]);
      setPaymentDrawerOpen(false);
      setOpen(false);

      if (outletData?._id) {
        navigate(`/${outletData._id}/success`, {
          replace: true,
          state: {
            razorpayOrderId: resolvedRazorpayOrderId,
          },
        });
      }
    };

    const buildStockCheckPayload = () => ({
      cartItems: cart.map((cartItem) => ({
        product: cartItem?.productId,
        quantity: Number(cartItem?.quantity || 1),
        addons: (Array.isArray(cartItem?.addons)
          ? cartItem.addons
          : []
        ).map((addon) => {
          const selectedValueId =
            addon?.selectedValueId ||
            addon?.valueId ||
            addon?._id ||
            null;

          return {
            _id:
              addon?.addonAllocationId ||
              addon?.addonId ||
              addon?._id,
            selectedValue:
              addon?.selectedValue ||
              addon?.label ||
              getAddonName(addon),
            price: Number(addon?.price || 0),
            selectedValueId,
            excludedOutlets: Array.isArray(addon?.excludedOutlets)
              ? addon.excludedOutlets
              : [],
            addonExcludedOutlets: Array.isArray(
              addon?.addonExcludedOutlets,
            )
              ? addon.addonExcludedOutlets
              : [],
          };
        }),
      })),
    });

    const handleProcessTransaction = async () => {
      if (cart.length === 0) {
        notify("Please add at least one item.", "warning");
        return;
      }

      if (!outletData?._id) {
        notify("Outlet information is missing.", "error");
        return;
      }

      try {
        setCheckingStock(true);

        const response = await axios.post(
          `${API.BASE_URL}raw-material/check-raw-material-stock-quick-order/${outletData._id}`,
          buildStockCheckPayload(),
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const responseData = response?.data?.data || response?.data;
        const isInStock =
          responseData?.inStock ??
          responseData?.isInStock ??
          (responseData?.isOutOfStock === true ? false : undefined) ??
          (responseData?.outOfStock === true ? false : undefined) ??
          response?.data?.status ??
          response?.data?.success ??
          true;

        if (isInStock === false) {
          setCartProductRawMaterials([]);
          notify(
            responseData?.error ||
              response?.data?.error ||
              responseData?.message ||
              responseData?.title ||
              response?.data?.message ||
              "Some items are out of stock.",
            "error",
          );
          return;
        }

        const checkedRawMaterials = Array.isArray(
          responseData?.cartProductRawMaterials,
        )
          ? responseData.cartProductRawMaterials
          : [];

        setCartProductRawMaterials(checkedRawMaterials);
        setOpen(false);
        setPaymentDrawerOpen(true);
      } catch (error) {
        const errorData = error?.response?.data;
        console.error("Error checking stock:", errorData || error);

        setCartProductRawMaterials([]);
        notify(
          errorData?.error ||
            errorData?.message ||
            error?.message ||
            "Unable to verify stock. Please try again.",
          "error",
        );
      } finally {
        setCheckingStock(false);
      }
    };

    return (
      <>
        {cartQuantity > 0 && (
          <Fab
            variant="extended"
            disableRipple
            onClick={() => setOpen(true)}
            sx={{
              position: "fixed",
              left: 0,
              bottom: 46,
              zIndex: 30,
              height: 60,
              minHeight: 60,
              px: 2,
              gap: 1,
              borderRadius: "0 30px 30px 0",
              backgroundColor: "#09A5F7",
              color: "#FFFFFF",
              textTransform: "none",
              transformOrigin: "left center",
              boxShadow: "0 10px 28px rgba(9,165,247,0.32)",

              animation: animateCartButton
                ? "cartAttention 650ms cubic-bezier(0.34, 1.56, 0.64, 1)"
                : "none",

              "@keyframes cartAttention": {
                "0%": {
                  transform: "translateX(-8px) scale(0.96)",
                },
                "35%": {
                  transform: "translateX(0) scale(1.08)",
                },
                "55%": {
                  transform: "translateX(0) scale(0.98)",
                },
                "75%": {
                  transform: "translateX(0) scale(1.03)",
                },
                "100%": {
                  transform: "translateX(0) scale(1)",
                },
              },

              "&:hover": {
                backgroundColor: "#008FD8",
              },

              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            <ShoppingCartRoundedIcon
              sx={{
                fontSize: 23,
              }}
            />

            <Box
              sx={{
                textAlign: "left",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 900,
                  lineHeight: 1.1,
                }}
              >
                View Cart
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 11,
                  fontWeight: 700,
                  opacity: 0.92,
                }}
              >
                {cartQuantity} {cartQuantity === 1 ? "item" : "items"}{" "}
              </Typography>
            </Box>
          </Fab>
        )}

        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => setOpen(false)}
          disableEnforceFocus
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: "none",
              height: "100dvh",
              maxHeight: "100dvh",
              borderRadius: 0,
              overflow: "hidden",
              backgroundColor: "#F5F9FB",
              position: "relative",
            },
          }}
        >
          <CartSection
            cart={cart}
            cartTaxable={cartTaxable}
            cartTaxAmount={taxAmount}
            cartTotal={cartTotal}
            discountAmount={discountAmount}
            grandTotal={grandTotal}
            cartQuantity={cartQuantity}
            checkingStock={checkingStock}
            onClose={() => setOpen(false)}
            onIncrease={(cartItemId) => updateCartQuantity(cartItemId, 1)}
            onDecrease={(cartItemId) => updateCartQuantity(cartItemId, -1)}
            onRemove={removeCartItem}
            outletData={outletData}
            onApplyCustomization={applyCartItemCustomization}
            onMessage={notify}
            onClear={clearCart}
            selectedOrderType={selectedOrderType}
            onOrderTypeChange={handleOrderTypeChange}
            onProcessTransaction={handleProcessTransaction}
          />
        </Drawer>

        <Drawer
          anchor="bottom"
          open={paymentDrawerOpen}
          onClose={handleClosePaymentDrawer}
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: "none",
              height: "100dvh",
              maxHeight: "100dvh",
              borderRadius: 0,
              overflow: "hidden",
              backgroundColor: "#F7F8FA",
            },
          }}
        >
          <Box
            className="custom-scrollbar"
            sx={{
              height: "100%",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
          >
            <PaymentProcessing
              embedded
              cart={cart}
              outletData={outletData}
              orderType={selectedOrderType}
              paymentMode="ONLINE"
              cartProductRawMaterials={cartProductRawMaterials}
              subtotal={cartTaxable}
              taxAmount={taxAmount}
              discountAmount={discountAmount}
              grandTotal={grandTotal}
              isCreatingOrder={isCreatingOrder}
              setIsCreatingOrder={setIsCreatingOrder}
              onClose={handleClosePaymentDrawer}
              onOrderCreated={handleOrderCreated}
              onOrderError={(errorMessage) =>
                notify(errorMessage || "Unable to create order.", "error")
              }
            />
          </Box>
        </Drawer>
      </>
    );
  },
);

CartDrawer.displayName = "CartDrawer";

const AnimatedCartItemCard = ({
  cartItem,
  outletData,
  onIncrease,
  onDecrease,
  onRemove,
  onApplyCustomization,
  onMessage,
}) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const handleDelete = () => {
    if (isRemoving) {
      return;
    }

    setIsRemoving(true);
  };

  return (
    <Box
      component={motion.div}
      initial={false}
      animate={
        isRemoving
          ? {
              x: "-110%",
              opacity: 0,
              scale: 0.96,
            }
          : {
              x: 0,
              opacity: 1,
              scale: 1,
            }
      }
      transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      }}
      onAnimationComplete={() => {
        if (isRemoving) {
          onRemove?.(cartItem?.cartItemId);
        }
      }}
      sx={{
        overflow: "hidden",
      }}
    >
      <CartItemCard
        cartItem={cartItem}
        outletData={outletData}
        onIncrease={onIncrease}
        onDecrease={onDecrease}
        onRemove={handleDelete}
        onApplyCustomization={onApplyCustomization}
        onMessage={onMessage}
      />
    </Box>
  );
};

const CartSection = ({
  cart,
  cartTaxable,
  cartTaxAmount,
  cartTotal,
  discountAmount = 0,
  grandTotal,
  cartQuantity,
  checkingStock = false,
  onClose,
  onIncrease,
  onDecrease,
  onRemove,
  outletData,
  onApplyCustomization,
  onMessage,
  onClear,
  selectedOrderType,
  onOrderTypeChange,
  selectedPaymentMethod,
  onPaymentMethodChange,
  onProcessTransaction,
}) => {
  const safeDiscountAmount = Number(discountAmount || 0);

  const payableTotal = Math.max(
    Number(grandTotal || cartTotal || 0) - safeDiscountAmount,
    0,
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: {
            xs: 1.5,
            sm: 3,
          },
          py: 1.4,
          zIndex: 3,
          backgroundColor: "#09A5F7",
          borderBottomLeftRadius: "18px",
          borderBottomRightRadius: "18px",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{
            width: "100%",
            maxWidth: 900,
            mx: "auto",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
              }}
            >
              <ShoppingCartRoundedIcon sx={{ color: "#fff" }} />
            </Box>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 20,
                  fontFamily: "Roboto Condensed, sans-serif",
                  fontWeight: 900,
                  color: "#fff",
                  lineHeight: 1.2,
                }}
              >
                Your Cart
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              onClick={onClose}
              aria-label="Close cart"
              sx={{
                color: "grey",
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "#E6F0F4",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Box
        className="custom-scrollbar"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: "#F5F5F5",
          px: {
            xs: 1.5,
            sm: 3,
          },
          py: 1.8,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 900,
            mx: "auto",
          }}
        >
          {cart.length > 0 ? (
            <Stack spacing={1.25}>
              {cart.map((cartItem) => (
                <AnimatedCartItemCard
                  key={cartItem?.cartItemId}
                  cartItem={cartItem}
                  outletData={outletData}
                  onIncrease={onIncrease}
                  onDecrease={onDecrease}
                  onRemove={onRemove}
                  onApplyCustomization={onApplyCustomization}
                  onMessage={onMessage}
                />
              ))}
            </Stack>
          ) : (
            <Paper
              elevation={0}
              sx={{
                minHeight: 330,
                px: 3,
                borderRadius: "20px",
                border: "1px dashed #B8D7E4",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Box>
                <ShoppingCartRoundedIcon
                  sx={{
                    fontSize: 54,
                    color: "#B9DCEB",
                  }}
                />

                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 18,
                    fontWeight: 900,
                    color: "#0E4961",
                  }}
                >
                  Your cart is empty
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    fontSize: 13,
                    color: "#718892",
                  }}
                >
                  Add something delicious to continue.
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: {
            xs: 1.5,
            sm: 3,
          },
          pt: 1.3,
          pb: "max(14px, env(safe-area-inset-bottom))",
          backgroundColor: "#FFFFFF",
          borderTop: "1px solid #DCEAF0",
          boxShadow: "0 -8px 28px rgba(14,73,97,0.08)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 900,
            mx: "auto",
          }}
        >
          <Stack spacing={1}>
            <Stack
              direction={{
                xs: "column",
                md: "row",
              }}
              spacing={1}
            >
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <SegmentedControls
                  value={selectedOrderType}
                  onChange={onOrderTypeChange}
                  options={[
                    {
                      value: "DINE_IN",
                      label: "Dine In",
                      icon: RestaurantIcon,
                    },
                    {
                      value: "TAKEAWAY",
                      label: "Takeaway",
                      icon: TakeoutDiningIcon,
                    },
                  ]}
                />
              </Box>
            </Stack>

            <Paper
              elevation={0}
              sx={{
                p: 1.2,
                borderRadius: "14px",
                border: "1px solid #DFEEF4",
                backgroundColor: "#F7FBFD",
              }}
            >
              <Stack spacing={0.8}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" alignItems="center" spacing={0.8}>
                    <ReceiptLongRoundedIcon
                      sx={{
                        color: "#09A5F7",
                        fontSize: 20,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#718892",
                        fontWeight: 700,
                      }}
                    >
                      Items
                    </Typography>
                  </Stack>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#97A8AF",
                      fontWeight: 700,
                    }}
                  >
                    {cartQuantity} {cartQuantity === 1 ? "item" : "items"}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#718892",
                      fontWeight: 700,
                    }}
                  >
                    Subtotal
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#97A8AF",
                      fontWeight: 700,
                    }}
                  >
                    {formatCurrency(cartTaxable)}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#718892",
                      fontWeight: 700,
                    }}
                  >
                    Tax
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#97A8AF",
                      fontWeight: 700,
                    }}
                  >
                    {formatCurrency(cartTaxAmount)}
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    pt: 0.4,
                    borderTop: "1px dashed #D5E4EA",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 900,
                      color: "#0E4961",
                    }}
                  >
                    Grand Total
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 900,
                      color: "#0E4961",
                    }}
                  >
                    {formatCurrency(payableTotal)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            <Button
              fullWidth
              variant="contained"
              disabled={cart.length === 0 || checkingStock}
              onClick={onProcessTransaction}
              sx={{
                minHeight: 52,
                borderRadius: "53px",
                color: "#FFFFFF",
                fontSize: 18.5,
                fontWeight: 900,
                textTransform: "none",
                background: "linear-gradient(180deg, #16B8EE 0%, #04A5E2 100%)",
                boxShadow: "0 10px 24px rgba(9,165,247,0.22)",
                fontFamily: "Roboto Condensed, sans-serif",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #008FD8 0%, #083A4F 100%)",
                  boxShadow: "0 12px 28px rgba(9,165,247,0.28)",
                },

                "&.Mui-disabled": {
                  background: "#C7D3D9",
                  color: "#FFFFFF",
                  boxShadow: "none",
                },
              }}
            >
              {checkingStock
                ? "Checking stock..."
                : `Place Order · ${formatCurrency(payableTotal)}`}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default CartDrawer;